import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider, ToggleButton, ToggleButtonGroup } from "@mui/material";

//components
import AddCompanyPopup from "../../components/AddCompanyPopup";
// store
import { messageAndErrorStore, projectStore, selectOptionsStore, userStore } from "../../store/Store";
// assets
import Close from "../../assets/close.svg";
import DropDown from "../../formComponents/DropDown";
import FormButton from "../../formComponents/FormButton";
// hooks
import { useEscape } from "../../hooks/useEscape";
import { useGetProjectDD } from "../../hooks/useGetProjectDD";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";
import Loading from "../../components/Loading";

export default function AddCarrier({ setTrigger, carrierId, setCarrierId }) {
  // global
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const currentProject = projectStore((data) => data.currentProject);
  const carrierOptions = selectOptionsStore((data) => data.carrierOptions);
  const setProjectCarriers = projectStore((data) => data.setProjectCarriers);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads,
  );
  const setProjectTotals = projectStore((data) => data.setProjectTotals);
  const projectCarriers = projectStore((data) => data.projectCarriers);
  const setProjectUnaddedCarriers = projectStore(
    (data) => data.setProjectUnaddedCarriers,
  );
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  var id = 0;
  if (carrierId) {
    id = carrierId;
  }
  const [carrier, setCarrier] = useState({
    id: 0,
    project_id: currentProject.project.id,
    pc_carrier_id: "",
    carrier_name: "",
    rate_sheet_id: 0,
    fsc_id: null,
    equipment_rate_id: 0,
    empty_boxes_rate_id: 0,
    full_boxes_rate_id: 0,
    apply_fsc_to_asset_loads: false,
    created_by_id: currentUser.id,
    updated_by_id: currentUser.id,
    cost_fsc_mileage_rate: 0,
    is_pass_through: false,
  });
  const [oldId, setOldId] = useState(null);
  const [pending, setPending] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [companyExists, setCompanyExists] = useState(true);
  const [useCostFscMileage, setUseCostFscMileage] = useState(false);
  const navigate = useNavigate();

  // populate options
  const { dropDowns } = useGetProjectDD();

  const colorTheme = createTheme({
    palette: {
      pxgreen: {
        main: "#4bad29",
        light: "#a9e496",
        dark: "#237508",
        contrastText: "#242105",
      },
      pxorange: {
        main: "#db8f0c",
        light: "#e8a633",
        dark: "#a47117",
        contrastText: "#fff",
      }
    },
  });

  useEffect(() => {
    if (JwtToken === "") {
      navigate("/login");
      return;
    }
    const controller = new AbortController();
    setIsPending(true);
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const ro = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    if (id && id !== "" && id !== 0) {
      fetch(`${process.env.REACT_APP_BACKEND}/pc/project/carrier/${id}`, ro)
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              navigate("/login");
            } else {
              toggleNewMessage("Something went wrong");
              return Promise.resolve();
            }
          } else {
            return response.json();
          }
        })
        .then((data) => {
          if (!data) {
            setIsPending(false);
            return;
          } else {
            setCarrier(data);
            setOldId(data.pc_carrier_id);
            if (data.cost_fsc_mileage_rate > 0) {
              setUseCostFscMileage(true);
            }
            setIsPending(false);
          }
        })
        .catch((err) => {
          if (err.name === "AbortError") {
            console.log("The fetch was aborted");
            setIsPending(false);
            return;
          } else {
            setIsPending(false);
            console.log(err);
          }
        });
      return () => {
        controller.abort();
      };
    } else {
      setIsPending(false);
    }
  }, [JwtToken, navigate, setCarrier, id, setOldId, toggleNewMessage]);

  const { exists } = useVerifyUnique(
    projectCarriers,
    "pc_carrier_id",
    carrier.pc_carrier_id,
    oldId,
  );

  const handleClose = () => {
    setCarrierId("");
    setTrigger(false);
  };

  useEscape(setTrigger, [setCarrierId], [""], pending);
  // check if company exists
  useEffect(() => {
    if (carrier.pc_carrier_id !== "") {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/company-exists?pc_carrier_id=${carrier.pc_carrier_id}&company_type=2`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              navigate("/login");
              setAuthStatus(false);
            } else {
              toggleNewMessage(`Something Went Wrong: ${res.status}`);
              return Promise.resolve();
            }
          }
          return res.json();
        })
        .then((data) => {
          if (data !== null) {
            if (data !== 0) {
              setCompanyExists(true);
            } else {
              setCompanyExists(false);
            }
          } else {
            return;
          }
        })
        .catch((_) => {
          toggleNewMessage("Something went wrong");
        });
      return () => controller.abort();
    }
  }, [carrier.pc_carrier_id, JwtToken, navigate, setAuthStatus, toggleNewMessage]);

  const handleSelect = (o, e) => {
    if (e.name === "pc_carrier_id") {
      setCarrier({
        ...carrier,
        carrier_name: o.label,
        pc_carrier_id: o.value,
      });
    } else if (e.name === "fsc_id") {
      if (o.value === 0) {
        setUseCostFscMileage(true);
        setCarrier({
          ...carrier,
          fsc_id: o.value,
        });
      } else {
        setCarrier({
          ...carrier,
          fsc_id: o.value,
        });
        setUseCostFscMileage(false);
      }
    } else {
      setCarrier({
        ...carrier,
        [e.name]: o.value,
      });
    }
  };
  // const handleFSC = () => {
  //   setCarrier({
  //     ...carrier,
  //     apply_fsc_to_asset_loads: !carrier.apply_fsc_to_asset_loads,
  //   });
  // };

  const handleFscMileage = (e) => {
    setCarrier({
      ...carrier,
      cost_fsc_mileage_rate: parseFloat(e.target.value),
    });
  };

  const handlePassThrough = () => {
    setCarrier({
      ...carrier,
      is_pass_through: !carrier.is_pass_through,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPending(true);
    if (exists) {
      toggleNewMessage("This Carrier has already been added to the project.");
      setPending(false);
      return;
    }
    var method = "POST";
    if (carrier.id > 0) {
      method = "PATCH";
    }
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    var reqBody = carrier;
    if (currentProject.project.use_propconnect_rates) {
      reqBody.rate_sheet_id = 0;
      reqBody.empty_boxes_rate_id = 0;
      reqBody.full_boxes_rate_id = 0;
      reqBody.equipment_rate_id = 0;
      reqBody.fsc_id = 0;
      reqBody.apply_fsc_to_asset_loads = false;
    }
    if (currentProject.fsc_area_id !== "" && !useCostFscMileage) {
      reqBody.cost_fsc_mileage_rate = 0;
    }
    const requestOptions = {
      method,
      headers,
      body: JSON.stringify(carrier),
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/project-carrier/${carrier.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          navigate("/login");
          setPending(false);
          setTrigger(false);
          return;
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          if (data.message === "project is archived cannot update") {
            toggleNewMessage("Project is Archived and cannot be updated.");
          } else {
            toggleNewMessage(data.message);
          }
          setPending(false);
        } else {
          setProjectCarriers(data.data.project_carriers);
          setProjectLoads(data.data.loads);
          setProjectTotals(data.data.all_totals);
          setProjectAssetLoads(data.data.asset_loads);
          setProjectUnaddedCarriers(data.data.project_unadded_carriers);
          setTrigger(false);
          setPending(false);
          toggleNewMessage(null, "Carrier Added");
        }
      })
      .catch((_) => {
        toggleNewMessage("Something went wrong");
        setPending(false);
      })
      .finally(() => setPending(false));
    return () => controller.abort();
  };

  if (isPending) {
    return <Loading />;
  }
  return (
    carrierOptions &&
    dropDowns && (
      <div className="popup">
        <div className="popup-inner">
          {!pending && (
            <div className="close">
              <img onClick={handleClose} src={Close} alt="close" />
            </div>
          )}
          <h3>Add Carrier Rate Sheet</h3>
          <form onSubmit={handleSubmit}>
            <DropDown
              name={"pc_carrier_id"}
              title={"Carrier"}
              options={carrierOptions}
              onChange={handleSelect}
              value={carrierOptions.filter(
                (x) => x.value === carrier.pc_carrier_id,
              )}
              placeholder={"Select Carrier"}
            />
            {!currentProject.project.use_propconnect_rates && (
              <>
                <DropDown
                  name={"rate_sheet_id"}
                  title={"Rate Sheet"}
                  options={dropDowns.rates}
                  onChange={handleSelect}
                  value={
                    dropDowns.rates &&
                    dropDowns.rates.filter(
                      (x) => x.value === carrier.rate_sheet_id,
                    )
                  }
                  placeholder={"Select Rate Sheet"}
                  required={true}
                />
                {currentProject.project.fsc_area_id !== "" && (
                  <DropDown
                    name={"fsc_id"}
                    title={"FSC Rate"}
                    options={dropDowns.fsc}
                    onChange={handleSelect}
                    value={
                      dropDowns.fsc &&
                      dropDowns.fsc.filter((x) => x.value === carrier.fsc_id)
                    }
                    placeholder={"Select FSC"}
                    required={
                      currentProject.project.customer_fsc_id === 0
                        ? false
                        : true
                    }
                  />
                )}
                {useCostFscMileage && (
                  <div className="mb-1 row field">
                    <label className="col-sm-4 col-form-label fw-bold">
                      FSC $/mile
                    </label>
                    <div className="col-sm-4">
                      <input
                        name="cost_fsc_mileage_rate"
                        type="number"
                        step={0.01}
                        required
                        onChange={handleFscMileage}
                        value={carrier.cost_fsc_mileage_rate || ""}
                      />
                    </div>
                  </div>
                )}
                <DropDown
                  name={"equipment_rate_id"}
                  title={"Equipment Rate"}
                  options={dropDowns.asset_rates}
                  onChange={handleSelect}
                  value={
                    dropDowns.asset_rates &&
                    dropDowns.asset_rates.filter(
                      (x) => x.value === carrier.equipment_rate_id,
                    )
                  }
                  placeholder={"Select Equipment Rate"}
                />
                <DropDown
                  name={"empty_boxes_rate_id"}
                  title={"Empty Boxes Rate"}
                  options={dropDowns.asset_rates}
                  onChange={handleSelect}
                  value={
                    dropDowns.asset_rates &&
                    dropDowns.asset_rates.filter(
                      (x) => x.value === carrier.empty_boxes_rate_id,
                    )
                  }
                  placeholder={"Select Empty Boxes Rate"}
                />
                <DropDown
                  name={"full_boxes_rate_id"}
                  title={"Full Boxes Rate"}
                  options={dropDowns.asset_rates}
                  onChange={handleSelect}
                  value={
                    dropDowns.asset_rates &&
                    dropDowns.asset_rates.filter(
                      (x) => x.value === carrier.full_boxes_rate_id,
                    )
                  }
                  placeholder={"Select Full Boxes Rate"}
                />
              </>
            )}
            <div className="mb-1 row field">
              <label className="col-sm-4 col-form-label">Pass-Through</label>
              <div className="col-auto">
              <ThemeProvider theme={colorTheme}>
                <ToggleButtonGroup
                  value={!carrier.is_pass_through ? "yes" : "no"}
                  exclusive
                  onChange={handlePassThrough}
                  aria-label="PassThrough"
                  size="small"
                >
                  <ToggleButton
                    value="no"
                    sx={{ borderRadius: "20px", bgcolor: "pxorange.main" }}
                  >
                    No
                  </ToggleButton>
                  <ToggleButton
                    value="yes"
                    sx={{ borderRadius: "20px", bgcolor: 'pxgreen.light' }}
                  >
                    Yes
                  </ToggleButton>
                </ToggleButtonGroup>
              </ThemeProvider>
              </div>
            </div>
            <div className="row-auto" style={{ textAlign: "left" }}>
              {/* {currentProject.project.fsc_area_id && (
                <label
                  className="col-auto form-check-label"
                  style={{ margin: "10px", fontWeight: "bold" }}
                  htmlFor="apply-fsc"
                >
                  Apply FSC to Asset Loads
                  <input
                    id="apply-fsc"
                    type="checkbox"
                    checked={carrier.apply_fsc_to_asset_loads}
                    onChange={handleFSC}
                    className="form-check-input"
                    style={{
                      borderColor: "black",
                      margin: "4px 0 0 8px",
                    }}
                  />
                </label>
              )} */}
              {!companyExists && (
                <AddCompanyPopup
                  setTrigger={setCompanyExists}
                  companyTypeId={2}
                  companyName={carrier.carrier_name}
                  pcCarrierId={carrier.pc_carrier_id}
                  pcDriverId={""}
                />
              )}
            </div>
            <FormButton pending={pending} name={"Submit"} />
          </form>
        </div>
      </div>
    )
  );
}
